import React, { useEffect, useMemo, useState } from "react";
import { Form, SelectPicker } from "rsuite";
import { useGlobalContext } from "../../../GlobalContext";
import NetworkService from "../../../NetworkService";
import {
  CompanyUser,
  findCompanyUserById,
  formatCompanyUserLabel,
  parseCompanyUsersResponse,
} from "../companyUsers";
import { DEFAULT_ORDER_FORM_LOCALE } from "../orderFormLocale";
import {
  getStoredPassengerUserId,
  setStoredPassengerUserId,
} from "../rideOrderPassengerStorage";
import { OrderFormLocale } from "../types";
import { ORDER_FORM_FIELD_PROPS } from "./orderFormRsuite";
import styles from "./orderForm.module.scss";

type PassengerSelectProps = {
  locale?: OrderFormLocale;
  userId: number | null;
  onUserSelect: (userId: number) => void;
  onClear: () => void;
};

function mergeUsers(primary: CompanyUser[], extra: CompanyUser | null): CompanyUser[] {
  if (!extra) return primary;
  if (findCompanyUserById(primary, extra.id)) return primary;
  return [extra, ...primary];
}

const PassengerSelect: React.FC<PassengerSelectProps> = ({
  locale,
  userId,
  onUserSelect,
  onClear,
}) => {
  const resolvedLocale = locale ?? DEFAULT_ORDER_FORM_LOCALE;
  const passengerLabels = resolvedLocale.passenger ?? DEFAULT_ORDER_FORM_LOCALE.passenger;
  const { user: userState } = useGlobalContext();
  const [admin] = userState;
  const authToken = admin?.authorisation?.token;

  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<CompanyUser | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!authToken || !NetworkService.jwtToken) return undefined;

    let cancelled = false;
    setIsLoading(true);

    NetworkService.getUsers({ page: 1, size: 100 }, debouncedSearch || undefined)
      .then((response) => {
        if (cancelled) return;
        setUsers(parseCompanyUsersResponse(response));
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authToken, debouncedSearch]);

  const resolvedUserId = userId ?? getStoredPassengerUserId();

  useEffect(() => {
    if (!resolvedUserId) {
      setSelectedUser(null);
      return;
    }

    if (!authToken || !NetworkService.jwtToken) return;

    let cancelled = false;

    NetworkService.getUserById(resolvedUserId)
      .then((response) => {
        if (cancelled) return;
        const user = response as CompanyUser;
        if (!user?.id) return;
        setSelectedUser(user);
      })
      .catch(() => {
        // Keep current selection on transient errors — do not clear LS or picker value.
      });

    return () => {
      cancelled = true;
    };
  }, [authToken, resolvedUserId]);

  useEffect(() => {
    if (!resolvedUserId) return;

    const fromList = findCompanyUserById(users, resolvedUserId);
    if (fromList) {
      setSelectedUser(fromList);
    }
  }, [users, resolvedUserId]);

  useEffect(() => {
    if (!selectedUser?.id || userId === selectedUser.id) return;
    onUserSelect(selectedUser.id);
  }, [userId, selectedUser, onUserSelect]);

  const visibleUsers = useMemo(
    () => mergeUsers(users, selectedUser),
    [users, selectedUser]
  );

  const pickerData = useMemo(
    () =>
      visibleUsers.map((user) => ({
        label: formatCompanyUserLabel(user),
        value: user.id,
        user,
      })),
    [visibleUsers]
  );

  return (
    <Form.Group controlId="ride-passenger" className={styles.passengerField}>
      <Form.ControlLabel className={styles.passengerLabel}>
        {passengerLabels?.label ?? "Ordering user"}
      </Form.ControlLabel>
      <SelectPicker
        {...ORDER_FORM_FIELD_PROPS}
        className={styles.rsControl}
        data={pickerData}
        value={resolvedUserId}
        placeholder={passengerLabels?.placeholder ?? "Select company user"}
        loading={isLoading}
        searchable
        preventOverflow
        container={() => document.body}
        menuClassName={styles.passengerSelectMenu}
        menuStyle={{ zIndex: 1100 }}
        onSearch={setSearch}
        onClean={() => {
          setStoredPassengerUserId(null);
          setSelectedUser(null);
          onClear();
        }}
        onChange={(value) => {
          if (value == null) return;

          const match = pickerData.find((item) => item.value === value);
          const user = match?.user;
          if (!user?.id) return;

          setSelectedUser(user);
          setStoredPassengerUserId(user.id);
          onUserSelect(user.id);
        }}
        renderMenu={(menu) => {
          if (isLoading) {
            return (
              <div className={styles.passengerMenuState}>
                {passengerLabels?.loading ?? "Loading users…"}
              </div>
            );
          }

          if (!pickerData.length) {
            return (
              <div className={styles.passengerMenuState}>
                {passengerLabels?.noResults ?? "No users found"}
              </div>
            );
          }

          return menu;
        }}
      />
    </Form.Group>
  );
};

export default PassengerSelect;
